import type {
  ActiveRoomsResponse,
  CreateRoomInput,
  IceConfigResponse,
  MyRoomsResponse,
  Room,
  RoomResponse,
  StartStreamInput,
  User,
} from "./types";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(
    status: number,
    code: string,
    message: string,
    details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

type ApiErrorBody = {
  error?: {
    code?: string;
    message?: string;
    details?: unknown;
  };
};

const AUTH_PATHS_WITHOUT_UNAUTHORIZED_EVENT = new Set([
  "/api/auth/login",
  "/api/auth/register",
  "/api/auth/me",
]);

async function request<T>(
  path: string,
  init: RequestInit & { skipUnauthorizedEvent?: boolean } = {},
): Promise<T> {
  const { skipUnauthorizedEvent, ...fetchInit } = init;
  const headers = new Headers(fetchInit.headers);
  if (fetchInit.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  let response: Response;
  try {
    response = await fetch(path, {
      ...fetchInit,
      headers,
      credentials: "include",
    });
  } catch {
    throw new ApiError(
      0,
      "NETWORK_ERROR",
      "Нет соединения с сервером. Проверьте сеть.",
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const isJson = response.headers
    .get("content-type")
    ?.includes("application/json");
  const body = isJson ? ((await response.json()) as T & ApiErrorBody) : null;

  if (!response.ok) {
    const code = body?.error?.code ?? "REQUEST_FAILED";
    const message = body?.error?.message ?? "Не удалось выполнить запрос.";
    if (
      response.status === 401 &&
      !skipUnauthorizedEvent &&
      !AUTH_PATHS_WITHOUT_UNAUTHORIZED_EVENT.has(path)
    ) {
      window.dispatchEvent(new Event("streamroom:unauthorized"));
    }
    throw new ApiError(response.status, code, message, body?.error?.details);
  }

  return body as T;
}

export const api = {
  register(input: {
    email: string;
    displayName: string;
    password: string;
    passwordConfirm: string;
  }): Promise<{ user: User }> {
    return request("/api/auth/register", {
      method: "POST",
      body: JSON.stringify(input),
      skipUnauthorizedEvent: true,
    });
  },

  login(input: { email: string; password: string }): Promise<{ user: User }> {
    return request("/api/auth/login", {
      method: "POST",
      body: JSON.stringify(input),
      skipUnauthorizedEvent: true,
    });
  },

  logout(): Promise<void> {
    return request("/api/auth/logout", { method: "POST" });
  },

  me(): Promise<{ user: User }> {
    return request("/api/auth/me", { skipUnauthorizedEvent: true });
  },

  activeRooms(): Promise<ActiveRoomsResponse> {
    return request("/api/rooms/active");
  },

  myRooms(): Promise<MyRoomsResponse> {
    return request("/api/rooms/mine");
  },

  createRoom(input: CreateRoomInput): Promise<RoomResponse> {
    return request("/api/rooms", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  getRoom(roomId: string): Promise<RoomResponse> {
    return request(`/api/rooms/${encodeURIComponent(roomId)}`);
  },

  joinRoom(roomId: string): Promise<RoomResponse> {
    return request(`/api/rooms/${encodeURIComponent(roomId)}/join`, {
      method: "POST",
    });
  },

  leaveRoom(roomId: string): Promise<void> {
    return request(`/api/rooms/${encodeURIComponent(roomId)}/leave`, {
      method: "POST",
    });
  },

  startRoom(roomId: string, input: StartStreamInput): Promise<RoomResponse> {
    return request(`/api/rooms/${encodeURIComponent(roomId)}/start`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  updateStream(roomId: string, input: StartStreamInput): Promise<RoomResponse> {
    return request(`/api/rooms/${encodeURIComponent(roomId)}/stream`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },

  stopRoom(roomId: string): Promise<RoomResponse> {
    return request(`/api/rooms/${encodeURIComponent(roomId)}/stop`, {
      method: "POST",
    });
  },

  iceConfig(): Promise<IceConfigResponse> {
    return request("/api/rtc/ice-config");
  },

  async ensureRoomLive(roomId: string): Promise<Room> {
    const response = await this.getRoom(roomId);
    if (response.room.status !== "live") {
      throw new ApiError(409, "ROOM_NOT_LIVE", "Трансляция ещё не началась.");
    }
    return response.room;
  },
};

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "Неизвестная ошибка.";
}
